import { CaretDownIcon, CheckIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useMemo, useRef, useState } from "react";

export type SmartComboboxOption = {
  value: string;
  label: string;
  keywords?: string[];
  prefix?: string;
};

type Props = {
  value: string;
  options: SmartComboboxOption[];
  placeholder?: string;
  emptyLabel?: string;
  allowCustom?: boolean;
  onChange: (value: string, option?: SmartComboboxOption) => void;
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export const SmartCombobox = ({
  value,
  options,
  placeholder = "Commencez à écrire…",
  emptyLabel = "Aucune suggestion",
  allowCustom = true,
  onChange,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredOptions = useMemo(() => {
    const query = normalize(value);
    if (!query) return options.slice(0, 8);
    return options
      .filter((option) =>
        normalize([option.label, ...(option.keywords ?? [])].join(" ")).includes(query),
      )
      .slice(0, 8);
  }, [options, value]);

  useEffect(() => setActiveIndex(0), [value]);

  useEffect(() => {
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);

  const select = (option: SmartComboboxOption) => {
    onChange(option.value, option);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <MagnifyingGlassIcon
        aria-hidden
        size={15}
        className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-slate-400"
      />
      <input
        value={value}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          if (allowCustom) onChange(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((index) => Math.min(index + 1, filteredOptions.length - 1));
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex((index) => Math.max(index - 1, 0));
          }
          if (event.key === "Enter" && open && filteredOptions[activeIndex]) {
            event.preventDefault();
            select(filteredOptions[activeIndex]);
          }
          if (event.key === "Escape") setOpen(false);
        }}
        className={cn(
          "min-h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-9 text-xs text-indigo-950 outline-none",
          "transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100",
        )}
      />
      <CaretDownIcon
        aria-hidden
        size={14}
        className={cn(
          "pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform",
          open && "rotate-180",
        )}
      />

      {open && (
        <div
          role="listbox"
          className="absolute inset-x-0 top-[calc(100%+6px)] z-40 max-h-56 overflow-y-auto rounded-xl border border-indigo-100 bg-white p-1.5 shadow-[0_16px_40px_rgba(30,27,75,0.14)] [scrollbar-color:rgb(165_180_252)_transparent] [scrollbar-width:thin]"
        >
          {filteredOptions.length ? (
            filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value === option.value}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => select(option)}
                className={cn(
                  "flex min-h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-xs font-medium text-indigo-950",
                  index === activeIndex ? "bg-indigo-50" : "hover:bg-slate-50",
                )}
              >
                {option.prefix && <span className="text-base leading-none">{option.prefix}</span>}
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {value === option.value && (
                  <CheckIcon size={13} weight="bold" className="text-emerald-500" />
                )}
              </button>
            ))
          ) : (
            <div className="px-3 py-3 text-center text-[11px] text-slate-500">
              {allowCustom ? `« ${value} » sera conservé comme valeur personnalisée.` : emptyLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
